import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import { AppLink } from '~/components/AppLink'
import { GoogleButton } from '~/components/GoogleButton'

import s from './Invited.module.css'

/**
 * An invitation, addressed to one person.
 *
 * A shared code is the whole door in one string — it goes in a group chat, gets forwarded,
 * and the only way to take it back locks out everybody who has not used it yet. This is a
 * row instead: spent once, withdrawn on its own, and readable afterwards to see who came in
 * on it.
 *
 * **Opening it does not spend it.** This page asks whether the invitation is still good;
 * it is spent when an account actually exists at the end of `/join`. A link preview fetching
 * what it points at must not burn somebody's invitation.
 */
export function Invited() {
  const { token = '' } = useParams()
  const [good, setGood] = useState<boolean | null>(null)

  useEffect(() => {
    let live = true
    fetch(`/inviting/${encodeURIComponent(token)}`, { credentials: 'same-origin' })
      .then(answer => (answer.ok ? answer.json() : { open: false }))
      .then((said: { open?: boolean }) => {
        if (live) setGood(Boolean(said.open))
      })
      .catch(() => {
        if (live) setGood(false)
      })
    return () => {
      live = false
    }
  }, [token])

  return (
    <div className={s.page}>
      <div className={s.sky} aria-hidden="true" />
      <main className={s.main}>
        <h1>{good === false ? 'London gigs' : "You're invited"}</h1>
        <div className={s.form}>
          {good === null ? <p className={s.who}>One moment…</p> : null}

          {good === false ? (
            <p className={s.note}>
              This invitation has been used already, or it has expired. Ask whoever sent it
              for another.
            </p>
          ) : null}

          {good ? (
            <>
              <p className={s.who}>
                A calendar of gigs worth going to in London, kept by hand. Sign in with
                Google and pick a name.
              </p>
              <GoogleButton href={`/auth/google?invite=${encodeURIComponent(token)}`} />
            </>
          ) : null}
          {/* On the card rather than under it: centring the page inside the frame put this
              line on a chrome highlight, and pale type on a pale reflection is unreadable. */}
          <p className={s.back}>
            <AppLink className={s.home} to="/">
              Have a look around first
            </AppLink>
          </p>
        </div>

      </main>
    </div>
  )
}
